import { FC, useEffect, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import PagedTable from '../../sorted-table/PagedTable';
import Loader from '../../loader/Loader';
import Tooltip from '../../tooltip/Tooltip';
import { showToast } from '../../../services/toast.service';
import { centsToDollars } from '../../../services/account.service';
import { getPaymentTransactions, getPaymentTransactionsOdata } from '../../../api/paymentGatewayApi';
import { getShippingPaymentTransactions } from '../../../api/shippingPaymentTransactionApi';
import { getMarketPlacePaymentTransaction } from '../../../api/marketPlacePaymentTransactionApi';
import { PaymentTransaction, ShippingPaymentTransaction, StandardParamsToODataParams } from '../../../types/interfaces';
import { MarketPlacePaymentTransaction } from '../../../types/interfaces/marketPlace.interfaces';
import { PaymentMethodStatus } from '../../../types/enums';
import { toastMessages } from '../../../constants/errorMessages';
import { APP_PATHS } from '../../../constants/routePaths';
import { LABEL_CONSTANTS, MARKETPLACE_CONSTANTS, PAYMENT_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../constants/common';
import './paymentHistory.scss';

const PaymentHistory: FC<{ accountId?: number; refresh?: number }> = ({ accountId, refresh }) => {
  const activeAccountId = useAppSelector(state => state.activeAccount.accountId);
  const paymentAccountId = accountId ?? activeAccountId;

  const [paymentHistory, setPaymentHistory] = useState<PaymentTransaction[]>([]);
  const [shippingPaymentHistory, setShippingPaymentHistory] = useState<ShippingPaymentTransaction[]>([]);
  const [marketPlacePaymentHistory, setMarketPlacePaymentHistory] = useState<MarketPlacePaymentTransaction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const currentRouteLocation = location?.pathname?.split('/');
  const appPage = currentRouteLocation && currentRouteLocation[2];

  useEffect(() => {
    handleGetPaymentHistory();
  }, [accountId, refresh]);

  useEffect(() => {
    handleGetShippingPaymentHistory();
  }, [accountId]);

  useEffect(() => {
    handleGetMarketPlacePaymentHistory();
  }, [paymentAccountId]);

  const handleGetPaymentHistory = async () => {
    try {
      setIsLoading(true);
      if (accountId) {
        const params = { filter: `accountId eq ${accountId}`, sort: '^billedDate' };
        const { data: paymentHistoryResponse } = await getPaymentTransactionsOdata(StandardParamsToODataParams(params));
        setPaymentHistory(paymentHistoryResponse);
      } else {
        const params = { sort: '^billedDate' };
        const { data: paymentHistoryResponse } = await getPaymentTransactions(params);
        setPaymentHistory(paymentHistoryResponse);
      }
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetShippingPaymentHistory = async () => {
    try {
      setIsLoading(true);
      const params = { filter: accountId ? `accountId eq ${accountId}` : undefined, sort: '^billedDate' };
      const { data: shippingPaymentHistoryResponse } = await getShippingPaymentTransactions(params);
      setShippingPaymentHistory(shippingPaymentHistoryResponse);
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetMarketPlacePaymentHistory = async () => {
    try {
      setIsLoading(true);
      const params = {
        filter: paymentAccountId ? `accountId eq ${paymentAccountId}` : undefined,
        sort: '^billedDate',
      };
      const { data: marketPlacePaymentHistoryResponse } = await getMarketPlacePaymentTransaction(params);
      setMarketPlacePaymentHistory(
        Array.isArray(marketPlacePaymentHistoryResponse)
          ? marketPlacePaymentHistoryResponse
          : marketPlacePaymentHistoryResponse.result ?? [],
      );
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(false);
    }
  };

  const getPaymentMethod = (history: any) => {
    const { method, maskedAccountNumber, notes } = history;
    let paymentLabel = '';

    switch (method) {
      case PaymentMethodStatus.DIRECT:
        paymentLabel = PAYMENT_CONSTANTS.DIRECT;
        break;
      case PaymentMethodStatus.NOT_AVAILABLE:
        paymentLabel = LABEL_CONSTANTS.NOT_AVAILABLE;
        break;
      case PaymentMethodStatus.CARD:
        paymentLabel = `${PAYMENT_CONSTANTS.CARD}....${maskedAccountNumber.slice(-4)}`;
        break;
      case PaymentMethodStatus.ACH:
        paymentLabel = `${PAYMENT_CONSTANTS.ACH}....${maskedAccountNumber.slice(-4)}`;
        break;
      case PaymentMethodStatus.EXTERNAL:
        paymentLabel = PAYMENT_CONSTANTS.EXTERNAL;
        break;
      default:
        paymentLabel = '-';
    }

    return (
      <>
        {paymentLabel}
        {(method === PaymentMethodStatus.DIRECT || method === PaymentMethodStatus.NOT_AVAILABLE) &&
          appPage &&
          Object.values(APP_PATHS).includes(appPage) && <Tooltip>{notes ?? '-NA-'}</Tooltip>}
      </>
    );
  };

  const getPaymentStatus = (status?: string) => (status === 'NotAttempted' ? 'Failed' : status ?? '-');

  const InfoLabel = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="payment-history-content">
      <label>{label}:</label>
      <label>{value}</label>
    </div>
  );

  return (
    <div className="payment-history-container">
      <div className="monthly-genetics-payment-history">
        <h3>{LABEL_CONSTANTS.MONTHLY_STORAGE_PAYMENT_HISTORY}</h3>
        <div className="desk-payment-history">
          <PagedTable
            headers={[
              { displayName: TABLE_HEADER_CONSTANTS.BILL_DATE, sortValue: 'billedDate' },
              { displayName: TABLE_HEADER_CONSTANTS.AMOUNT },
              { displayName: TABLE_HEADER_CONSTANTS.AVERAGE_DAILY_BALANCE },
              { displayName: TABLE_HEADER_CONSTANTS.PAYMENT_METHOD },
              { displayName: TABLE_HEADER_CONSTANTS.REFERENCE_ID },
              { displayName: TABLE_HEADER_CONSTANTS.STATUS },
            ]}
            sortBy="^billedDate"
            filter={accountId ? `accountId eq ${accountId}` : ''}
            getData={params => {
              return accountId
                ? getPaymentTransactionsOdata(StandardParamsToODataParams(params))
                : getPaymentTransactions(params);
            }}
            buildRow={history => {
              return [
                `${new Date(history?.billedDate).toLocaleDateString('en-US')}`,
                `$${centsToDollars(history?.costInCents)}`,
                history?.averageDailyBalance,
                getPaymentMethod(history),
                history?.transactionReferenceId,
                getPaymentStatus(history?.status),
              ];
            }}
            height={240}
            pageSize={6}
            noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
            refresh={refresh}
          />
        </div>
        <div className="xs-payment-history">
          {!isLoading && paymentHistory?.length === 0 ? (
            <div className="result-item">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
          ) : (
            <>
              {paymentHistory &&
                paymentHistory.map(history => {
                  return (
                    <div key={history.paymentTransactionId} className="card payment-history-card">
                      <div className="payment-history-info">
                        <InfoLabel
                          label={TABLE_HEADER_CONSTANTS.BILL_DATE}
                          value={new Date(history?.billedDate).toLocaleDateString('en-US') ?? '-'}
                        />
                        <InfoLabel
                          label={TABLE_HEADER_CONSTANTS.AMOUNT}
                          value={`$${centsToDollars(history?.costInCents) ?? '0'}`}
                        />
                        <InfoLabel
                          label={TABLE_HEADER_CONSTANTS.AVERAGE_DAILY_BALANCE}
                          value={`${history?.averageDailyBalance ?? '-'}`}
                        />
                      </div>
                      <div className="payment-history-info">
                        <InfoLabel label={TABLE_HEADER_CONSTANTS.PAYMENT_METHOD} value={getPaymentMethod(history)} />
                        <InfoLabel
                          label={TABLE_HEADER_CONSTANTS.REFERENCE_ID}
                          value={`${history?.transactionReferenceId ?? '-'}`}
                        />
                        <InfoLabel label={TABLE_HEADER_CONSTANTS.STATUS} value={getPaymentStatus(history?.status)} />
                      </div>
                    </div>
                  );
                })}
            </>
          )}
          {isLoading && <Loader addedSpace loaderSize="small" />}
        </div>
      </div>

      <div className="shipping-insurance-payment-history">
        <h3>{LABEL_CONSTANTS.SHIPPING_PAYMENT_HISTORY}</h3>
        <div className="desk-payment-history">
          <PagedTable
            headers={[
              { displayName: TABLE_HEADER_CONSTANTS.BILL_DATE, sortValue: 'billedDate' },
              { displayName: TABLE_HEADER_CONSTANTS.AMOUNT },
              { displayName: TABLE_HEADER_CONSTANTS.PAYMENT_METHOD },
              { displayName: TABLE_HEADER_CONSTANTS.REFERENCE_ID },
              { displayName: TABLE_HEADER_CONSTANTS.STATUS },
            ]}
            sortBy="^billedDate"
            filter={accountId ? `accountId eq ${accountId}` : undefined}
            getData={getShippingPaymentTransactions}
            buildRow={history => {
              return [
                `${new Date(history?.billedDate).toLocaleDateString('en-US')}`,
                `$${centsToDollars(history?.totalAmountPaidCents)}`,
                getPaymentMethod(history),
                history?.transactionReferenceId,
                getPaymentStatus(history?.status),
              ];
            }}
            height={240}
            pageSize={6}
            noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
            refresh={refresh}
          />
        </div>
        <div className="xs-payment-history">
          {!isLoading && shippingPaymentHistory?.length === 0 ? (
            <div className="result-item">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
          ) : (
            <>
              {shippingPaymentHistory &&
                shippingPaymentHistory.map(history => {
                  return (
                    <div key={history.shippingPaymentTransactionId} className="card payment-history-card">
                      <div className="payment-history-info">
                        <InfoLabel
                          label={TABLE_HEADER_CONSTANTS.BILL_DATE}
                          value={new Date(history?.billedDate).toLocaleDateString('en-US') ?? '-'}
                        />
                        <InfoLabel
                          label={TABLE_HEADER_CONSTANTS.AMOUNT}
                          value={`$${centsToDollars(history?.totalAmountPaidCents) ?? '0'}`}
                        />
                      </div>
                      <div className="payment-history-info">
                        <InfoLabel label={TABLE_HEADER_CONSTANTS.PAYMENT_METHOD} value={getPaymentMethod(history)} />
                        <InfoLabel
                          label={TABLE_HEADER_CONSTANTS.REFERENCE_ID}
                          value={`${history?.transactionReferenceId ?? '-'}`}
                        />
                        <InfoLabel label={TABLE_HEADER_CONSTANTS.STATUS} value={getPaymentStatus(history?.status)} />
                      </div>
                    </div>
                  );
                })}
            </>
          )}
          {isLoading && <Loader addedSpace loaderSize="small" />}
        </div>
      </div>

      <div className="marketplace-payment-history">
        <h3>{MARKETPLACE_CONSTANTS.MARKETPLACE_PAYMENTS_HISTORY}</h3>
        <div className="desk-payment-history">
          <PagedTable
            headers={[
              { displayName: TABLE_HEADER_CONSTANTS.BILL_DATE, sortValue: 'billedDate' },
              { displayName: TABLE_HEADER_CONSTANTS.AMOUNT },
              { displayName: TABLE_HEADER_CONSTANTS.PAYMENT_METHOD },
              { displayName: TABLE_HEADER_CONSTANTS.REFERENCE_ID },
              { displayName: TABLE_HEADER_CONSTANTS.STATUS },
            ]}
            sortBy="^billedDate"
            filter={paymentAccountId ? `accountId eq ${paymentAccountId}` : undefined}
            getData={getMarketPlacePaymentTransaction}
            buildRow={history => {
              return [
                `${new Date(history?.billedDate).toLocaleDateString('en-US')}`,
                `$${centsToDollars(history?.totalAmountPaidCents)}`,
                history?.paymentMethod ?? '-',
                history?.transactionReferenceId ?? '-',
                getPaymentStatus(history?.paymentStatus),
              ];
            }}
            height={240}
            pageSize={6}
            noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
            refresh={refresh}
          />
        </div>
        <div className="xs-payment-history">
          {!isLoading && marketPlacePaymentHistory?.length === 0 ? (
            <div className="result-item">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
          ) : (
            <>
              {marketPlacePaymentHistory &&
                marketPlacePaymentHistory.map(history => {
                  return (
                    <div key={history.marketPlacePaymentTransactionId} className="card payment-history-card">
                      <div className="payment-history-info">
                        <InfoLabel
                          label={TABLE_HEADER_CONSTANTS.BILL_DATE}
                          value={new Date(history?.billedDate).toLocaleDateString('en-US') ?? '-'}
                        />
                        <InfoLabel
                          label={TABLE_HEADER_CONSTANTS.AMOUNT}
                          value={`$${centsToDollars(history?.totalAmountPaidCents) ?? '0'}`}
                        />
                      </div>
                      <div className="payment-history-info">
                        <InfoLabel label={TABLE_HEADER_CONSTANTS.PAYMENT_METHOD} value={history?.paymentMethod ?? '-'} />
                        <InfoLabel
                          label={TABLE_HEADER_CONSTANTS.REFERENCE_ID}
                          value={`${history?.transactionReferenceId ?? '-'}`}
                        />
                        <InfoLabel label={TABLE_HEADER_CONSTANTS.STATUS} value={getPaymentStatus(history?.paymentStatus)} />
                      </div>
                    </div>
                  );
                })}
            </>
          )}
          {isLoading && <Loader addedSpace loaderSize="small" />}
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory;
